import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  styled
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useSearchParam } from 'helpers/useSearchParam';
import { allResultTypes } from 'helpers/jobResultType';
import { useExecutionsContext } from '../ExecutionsContext';

const FilterPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const FilterField = styled(Box)({
  minWidth: 160,
  flex: '1 1 160px',
  maxWidth: 240
});

const WideFilterField = styled(Box)({
  minWidth: 180,
  flex: '1 1 200px',
  maxWidth: 280
});

const DateField = styled(Box)({
  minWidth: 150,
  flex: '0 1 160px'
});

const clearableInputProps = (value, onClear) => ({
  endAdornment: value ? (
    <InputAdornment position="end">
      <IconButton
        aria-label="Clear filter"
        edge="end"
        onClick={onClear}
        size="small"
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ) : null
});

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder
}) => {
  const selected =
    options.find((option) => option.value === value) || null;

  return (
    <FilterField>
      <Autocomplete
        clearOnEscape
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, selectedValue) =>
          option.value === selectedValue.value
        }
        onChange={(_event, next) => onChange(next?.value || '')}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder || 'Search…'}
            size="small"
          />
        )}
        size="small"
        value={selected}
      />
    </FilterField>
  );
};

const ClearableTextFilter = ({
  label,
  value,
  onChange,
  placeholder
}) => (
  <WideFilterField>
    <TextField
      fullWidth
      InputProps={clearableInputProps(value, () => onChange(''))}
      label={label}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      size="small"
      value={value || ''}
    />
  </WideFilterField>
);

const ClearableDateFilter = ({ label, value, onChange, min }) => (
  <DateField>
    <TextField
      fullWidth
      InputLabelProps={{ shrink: true }}
      InputProps={clearableInputProps(value, () => onChange(''))}
      inputProps={{ min }}
      label={label}
      onChange={(event) => onChange(event.target.value)}
      size="small"
      type="date"
      value={value || ''}
    />
  </DateField>
);

const ExecutionsToolbar = () => {
  const {
    jobTypes,
    companies,
    partners,
    onTypeChange,
    onCompanyChange,
    onPartnerChange,
    onStatusChange,
    onJobNameChange,
    onJobIdChange,
    onFromChange,
    onToChange,
    onClearAll
  } = useExecutionsContext();

  const [type] = useSearchParam('type');
  const [company] = useSearchParam('company');
  const [partner] = useSearchParam('partner');
  const [status] = useSearchParam('status');
  const [jobName] = useSearchParam('jobName');
  const [jobId] = useSearchParam('jobId');
  const [from] = useSearchParam('from');
  const [to] = useSearchParam('to');

  const typeOptions = jobTypes.map((jobType) => ({
    value: jobType,
    label: jobType
  }));

  const companyOptions = companies.map((companyName) => ({
    value: companyName || 'no-company',
    label: companyName || 'No company in job'
  }));

  const partnerOptions = partners.map((partnerName) => ({
    value: partnerName || 'no-partner',
    label: partnerName || 'No partner in job'
  }));

  const statusOptions = allResultTypes.map((resultType) => ({
    value: resultType,
    label: resultType.charAt(0).toUpperCase() + resultType.slice(1)
  }));

  const hasActiveFilters = Boolean(
    type ||
      company ||
      partner ||
      status ||
      jobName ||
      jobId ||
      from ||
      to
  );

  return (
    <FilterPanel>
      <ClearableTextFilter
        label="Name"
        onChange={onJobNameChange}
        placeholder="Filter by job name"
        value={jobName}
      />
      <SearchableSelect
        label="Type"
        onChange={onTypeChange}
        options={typeOptions}
        placeholder="Search type"
        value={type}
      />
      <SearchableSelect
        label="Company"
        onChange={onCompanyChange}
        options={companyOptions}
        placeholder="Search company"
        value={company}
      />
      <SearchableSelect
        label="Partner"
        onChange={onPartnerChange}
        options={partnerOptions}
        placeholder="Search partner"
        value={partner}
      />
      <SearchableSelect
        label="Status"
        onChange={onStatusChange}
        options={statusOptions}
        placeholder="Search status"
        value={status}
      />
      <ClearableTextFilter
        label="Job ID"
        onChange={onJobIdChange}
        placeholder="Filter by job id"
        value={jobId}
      />
      <ClearableDateFilter
        label="From"
        onChange={onFromChange}
        value={from}
      />
      <ClearableDateFilter
        label="To"
        min={from || undefined}
        onChange={onToChange}
        value={to}
      />
      <Button
        color="secondary"
        disabled={!hasActiveFilters}
        onClick={onClearAll}
        size="small"
        variant="outlined"
      >
        Clear all
      </Button>
    </FilterPanel>
  );
};

export default ExecutionsToolbar;
